<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  toRefs,
} from 'vue';
import { compact } from 'lodash';
import { precio } from '../../../../../libs/cifra/importes';
import Overlay from '../../../../../libs/cifra/overlay';
import SelectorModalidad from '../../SelectorModalidad.vue';
import SelectorNumerico from '../../../../../components/inputs/SelectorNumerico.vue';
import InputCheckboxBoolean from '../../../../../components/cifra/InputCheckboxBoolean.vue';

const MENSUAL_ID = 2;

// Props
const props = defineProps({
  infoServicio: {
    type: Object,
    required: true,
  },
  overlay: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(['siguiente', 'modalidades-cargadas', 'form-valido']);

// Template refs
const selectModalidad = ref();

// Reactive data
const showFechaFin = ref(false);
const puedeFraccionar = ref(false);

// Overlay instance
let overlayInstance = null;

// Make props reactive
const { infoServicio } = toRefs(props);

// Computed properties
const formValido = computed(() => {
  const valores = compact([
    infoServicio.value.modalidad_id,
    infoServicio.value.fecha_alta,
    infoServicio.value.fecha_cobro,
  ]);
  return valores.length === 3;
});

const importeModalidad = computed(() => {
  if (infoServicio.value.importe_modalidad === undefined) {
    return '';
  }
  return precio(infoServicio.value.importe_modalidad);
});

// Methods
const cargarCuotas = () => {
  const porcentaje = +parseFloat(100 / infoServicio.value.numero_cuotas).toFixed(2);
  const cuotas = [];
  for (let i = 0; i < infoServicio.value.numero_cuotas; i += 1) {
    const newDate = new Date();
    newDate.setMonth(newDate.getMonth() + i + 1);
    cuotas.push({
      fecha: newDate,
      porcentaje,
    });
  }
  infoServicio.value.cuotas = cuotas;
};

const setModalidad = (id, periodicidadId, importeConIva, servicioTipo) => {
  const esMensual = Number.parseInt(periodicidadId, 10) === MENSUAL_ID;
  showFechaFin.value = esMensual;
  puedeFraccionar.value = !esMensual;
  infoServicio.value.modalidad_id = id;
  infoServicio.value.mensual = esMensual;
  infoServicio.value.importe_modalidad = importeConIva;
  infoServicio.value.servicio_tipo = servicioTipo;
  if (infoServicio.value.fraccionar && !puedeFraccionar.value) {
    // Si estaba marcado fraccionar lo quitamos porque no se puede
    // fraccionar un servicio mensual
    infoServicio.value.fraccionar = false;
  }
  if (!showFechaFin.value) {
    infoServicio.value.fecha_fin = null;
  }
};

const modalidadesCargadas = (modalidades) => {
  emit('modalidades-cargadas', modalidades);
};

// Watchers
watch(
  () => infoServicio.value.modalidad_id,
  () => {
    // [0] porque solo habrá una selección, no permitimos más
    const selectedData = selectModalidad.value?.selectedData()[0];
    if (!selectedData) {
      return;
    }

    setModalidad(
      selectedData.id,
      selectedData.periodicidad.id,
      selectedData.importe_con_iva,
      selectedData.servicio.tipo,
    );
  },
);

watch(
  () => infoServicio.value.fraccionar,
  (valor) => {
    if (valor) {
      cargarCuotas();
    } else {
      infoServicio.value.cuotas = [];
    }
  },
);

watch(
  () => infoServicio.value.numero_cuotas,
  () => {
    cargarCuotas();
  },
);

watch(
  () => props.overlay,
  (value) => {
    if (overlayInstance) {
      if (value) {
        overlayInstance.show();
      } else {
        overlayInstance.hide();
      }
    }
  },
);

watch(
  formValido,
  (value) => {
    emit('form-valido', value);
  },
);

// Lifecycle
onMounted(() => {
  // Get the DOM element from the current component
  const element = document.getElementById('info_servicio')
    || document.querySelector('#template-info-servicio');
  if (element) {
    overlayInstance = new Overlay(element);
    if (props.overlay) {
      overlayInstance.show();
    }
  }
});
</script>

<template id="template-info-servicio">
  <div>
    <div class="card bg-secondary m-t">
      <form id="info_servicio" autocomplete="off">
        <div class="card-header border-bottom-cifra">
          <a
            href="#"
            class="btn btn-sm pull-right"
            :class="{'btn-default': !formValido, 'btn-success': formValido }"
            :disabled="formValido ? null : 'disabled'"
            @click.prevent="formValido ? $emit('siguiente') : null"
          >
            {{ $I18n.t('Siguiente') }} <i class="fa fa-chevron-right"></i>
          </a>
          {{ $I18n.t('informacion_del_servicio') }}
        </div>
        <div class="card-body can-overflow">
          <div class="row">
            <div class="col-md-6">
              <div class="row">
                <div class="col-xs-8">
                  <div class="form-group">
                    <label class="control-label">{{ $I18n.t('concepto_modalidad') }}</label>
                    <SelectorModalidad
                      ref="selectModalidad"
                      v-model="infoServicio.modalidad_id"
                      :data-placeholder="$I18n.t('Seleccione')"
                      style="width:100%"
                      @modalidades-cargadas="modalidadesCargadas"
                    />
                  </div>
                </div>
                <div
                  v-if="infoServicio.modalidad_id"
                  class="col-xs-4 label label-success label-as-form-control
                    text-16x text-bold text-nowrap"
                >
                  {{ importeModalidad }}
                </div>
              </div>
              <div class="row">
                <div class="col-sm-4">
                  <div class="form-group">
                    <label class="control-label">{{ $I18n.t('fecha_inscripcion') }}</label>
                    <input-fecha-hora v-model="infoServicio.fecha_alta" />
                  </div>
                </div>
                <div class="col-sm-4">
                  <div class="form-group">
                    <label class="control-label">
                      {{ $I18n.t('fecha_inicio_actividad.large') }}
                    </label>
                    <input-fecha-hora v-model="infoServicio.fecha_cobro" />
                  </div>
                </div>
                <div class="col-sm-4" style="z-index: 100">
                  <div v-show="showFechaFin" class="animated fadeInDown">
                    <div class="form-group">
                      <label class="control-label">
                        {{ $I18n.t('Fecha_fin') }}
                        <i
                          class="fa fa-info-circle"
                          data-toggle="tooltip"
                          :data-title="$I18n.t('fecha_fin_help')"
                        ></i>
                      </label>
                      <input-fecha-hora v-model="infoServicio.fecha_fin" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
              </div>
            </div>
            <div class="col-md-6">
              <transition enter-active-class="animated fadeInDown">
                <div v-if="puedeFraccionar" class="row">
                  <div class="col-sm-4 m-t-lg">
                    <div class="form-group">
                      <InputCheckboxBoolean
                        v-model="infoServicio.fraccionar"
                        tipo="switch"
                      >
                        {{ $I18n.t('Fraccionar_pago') }}
                      </InputCheckboxBoolean>
                    </div>
                  </div>
                  <transition enter-active-class="animated fadeInRight">
                    <div v-if="infoServicio.fraccionar" class="col-sm-8 text-center">
                      <label class="control-label">{{ $I18n.t('n_cuotas') }}</label>
                      <SelectorNumerico
                        v-model.number="infoServicio.numero_cuotas"
                        size="sm"
                        :min="1"
                        :max="99"
                        class="input-3-digitos"
                        style="margin: auto;"
                      />
                    </div>
                  </transition>
                </div>
              </transition>
              <transition enter-active-class="animated fadeInRight">
                <div v-if="infoServicio.fraccionar">
                  <table class="table table-striped table-form">
                    <thead>
                      <tr>
                        <th class="col-md-8"></th>
                        <th class="col-md-4">
                          {{ $I18n.t('Fecha') }}
                        </th>
                        <th class="col-md-2">
                          %
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(cuota, index) in infoServicio.cuotas" :key="index">
                        <td>{{ $I18n.t('cuota') }} {{ index + 1 }}</td>
                        <td>
                          <input-fecha-hora v-model="cuota.fecha" />
                        </td>
                        <td>{{ cuota.porcentaje }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </transition>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label class="control-label">{{ $I18n.t('observaciones_privadas') }}</label>
                <textarea v-model="infoServicio.observaciones_privadas" class="form-control" />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
